import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { BulkTagSettingDialogProps, BodyPartCategory, BodyPart } from './types';
import bodyPartData from '../../config/bodyPartTags.json';
import styles from './BulkTagSettingDialog.module.css';

/**
 * 一括タグ設定ダイアログコンポーネント
 */
const BulkTagSettingDialog: React.FC<BulkTagSettingDialogProps> = ({
  isOpen,
  onClose,
  onSelectBodyPart
}) => {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // アコーディオンの開閉を切り替え
  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  // 部位選択時の処理
  const handlePartSelect = (part: BodyPart) => {
    onSelectBodyPart(part);
    onClose();
  };

  if (!isOpen) return null;

  const categories = bodyPartData.bodyPartCategories as BodyPartCategory[];

  const modalContent = (
    <div className={styles['bulk-tag-setting-dialog__overlay']}>
      <div className={styles['bulk-tag-setting-dialog__container']}>
        <div className={styles['bulk-tag-setting-dialog__header']}>
          <h2 className={styles['bulk-tag-setting-dialog__title']}>
            部位から一括設定
          </h2>
          <button
            type="button"
            className={styles['bulk-tag-setting-dialog__close-button']}
            onClick={onClose}
            aria-label="ダイアログを閉じる"
          >
            <FaTimes />
          </button>
        </div>
        
        <div className={styles['bulk-tag-setting-dialog__content']}>
          <p className={styles['bulk-tag-setting-dialog__description']}>
            部位を選択すると、関連するタグが自動的に追加されます。
          </p>
          
          <div className={styles['bulk-tag-setting-dialog__accordion']}>
            {categories.map((category) => (
              <div
                key={category.id}
                className={styles['bulk-tag-setting-dialog__category']}
              >
                <button
                  type="button"
                  className={styles['bulk-tag-setting-dialog__category-header']}
                  onClick={() => toggleCategory(category.id)}
                  aria-expanded={expandedCategories.has(category.id)}
                >
                  <span className={styles['bulk-tag-setting-dialog__category-icon']}>
                    <FaChevronRight />
                  </span>
                  <span className={styles['bulk-tag-setting-dialog__category-title']}>
                    {category.title}
                  </span>
                </button>
                
                <div 
                  className={`${styles['bulk-tag-setting-dialog__category-content']} ${
                    expandedCategories.has(category.id) 
                      ? styles['bulk-tag-setting-dialog__category-content--expanded'] 
                      : styles['bulk-tag-setting-dialog__category-content--collapsed']
                  }`}
                >
                  {category.parts.map((part) => (
                    <button
                      key={part.id}
                      type="button"
                      className={styles['bulk-tag-setting-dialog__part-button']}
                      onClick={() => handlePartSelect(part)}
                    >
                      <span className={styles['bulk-tag-setting-dialog__part-name']}>
                        {part.name}
                      </span>
                      <span className={styles['bulk-tag-setting-dialog__part-tags']}>
                        {part.tags.join(', ')}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // UploadModalと同じように、body直下にPortalとしてレンダリング
  return createPortal(modalContent, document.body);
};

export default BulkTagSettingDialog; 